import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaUser, FaCog, FaQuestionCircle, FaSignOutAlt, FaTimes, FaBars } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { logoutUser } from "../../services/authService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n/config";
import AuthLoader from "../Login/AuthLoader";

const Navbar = () => {
    const navigate = useNavigate();

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const dropdownRef = useRef(null);
    const { user, refreshToken, logout } = useAuth();
    const { i18n } = useTranslation();
    const [selectedLang, setSelectedLang] = useState("");
    const [logoutLoading, setLogoutLoading] = useState(false);

    useEffect(() => {
        if (i18n.language) {
            // Normalize 'en-US' to 'en'
            const lang = i18n.language.split('-')[0];
            setSelectedLang(lang);
        }
    }, [i18n.language]);

    const changeLanguage = (e) => {
        const newLang = e.target.value;
        i18n.changeLanguage(newLang);
        setSelectedLang(newLang);
        localStorage.setItem('i18nextLng', newLang);
    };

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const MIN_LOGOUT_LOADING_TIME = 2000;

    // Logout handler
    const handleLogout = async () => {
        setLogoutLoading(true);

        try {
            await Promise.all([
                logoutUser(refreshToken),          // your API call
                delay(MIN_LOGOUT_LOADING_TIME),    // compulsory loader time
            ]);
        } catch (err) {
            console.error("Logout API failed", err);
            // Even if API fails, proceed to local logout for safety
        } finally {
            logout(); // clear tokens
            navigate("/smartcity/login", { replace: true });
            setLogoutLoading(false);
        }
    };



    // Click outside closes dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (!user) return null;

    return (
        <>
            {logoutLoading && <AuthLoader text="Signing you out securely..." />}
            <nav className="bg-white shadow px-6 py-3 flex justify-between items-center relative z-[1000]">
                <div className="font-medium text-lg">Demo Muncipal Corporation</div>

                {/* Right section */}
                <div className="hidden md:flex flex-col flex flex-col items-end" ref={dropdownRef}>
                    {/* Row 1: Language + Department */}
                    <div className="flex items-center space-x-4">
                        <select
                            id="Selectedlang"
                            name="Selectedlang"
                            value={selectedLang}
                            onChange={changeLanguage} // ✅ Call setLang
                            className="rounded-md border cursor-pointer border-[#d4d4d4] bg-white px-3 py-1 text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="en">English</option>
                            <option value="mr">मराठी</option>
                            <option value="hi">हिन्दी</option>
                            {/* <option value="gj">ગુજરાતી</option> */}
                        </select>

                        <span className="text-gray-700 text-sm">
                            Department : <strong>માહિતી ટેકનોલોજી (IT)</strong>
                        </span>
                    </div>

                    {/* Row 2: User dropdown */}
                    <div className="relative mt-1">
                        <button
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                            className="flex items-center text-gray-700 text-sm focus:outline-none"
                        >
                            <span className="mr-1">User : {user.username}</span>
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </button>

                        {dropdownOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg py-2 z-10">
                                <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                    <FaUser className="mr-2" /> Profile
                                </button>
                                <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                    <FaCog className="mr-2" /> Setting
                                </button>
                                <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                    <FaQuestionCircle className="mr-2" /> Help
                                </button>
                                <button
                                    onClick={handleLogout}
                                    disabled={logoutLoading}
                                    className={`flex w-full items-center px-4 py-2 text-sm 
  ${logoutLoading ? "opacity-60 cursor-not-allowed" : "text-red-600 hover:bg-gray-100"}`}
                                >
                                    <FaSignOutAlt className="mr-2" />
                                    {logoutLoading ? "Signing out..." : "Log Out"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                {/* Mobile view: Language + Toggle */}
                <div className="flex items-center transition-all duration-300 ease-in-out space-x-3 md:hidden">
                    <select
                        id="Selectedlang"
                        name="Selectedlang"
                        value={selectedLang}
                        onChange={changeLanguage}
                        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                        <option value="en">English</option>
                        {/* <option value="gj">ગુજરાતી</option> */}
                        <option value="mr">मराठी</option>
                        <option value="hn">हिन्दी</option>
                    </select>

                    <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                        {mobileMenuOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
                    </button>
                </div>

                {/* Mobile Dropdown Menu */}
                {/* Mobile Dropdown Menu */}
                <div
                    className={`absolute top-full right-0 bg-white w-full shadow-md border-t md:hidden transform transition-all duration-300 ease-in-out origin-top 
    ${mobileMenuOpen ? "scale-y-100 opacity-100" : "scale-y-0 opacity-0 pointer-events-none"}`}
                >
                    <div className="flex flex-col items-start p-4 space-y-2">
                        <span className="text-gray-700 text-sm">
                            Department : <strong>માહિતી ટેકનોલોજી (IT)</strong>
                        </span>

                        <button className="flex items-center text-gray-700 text-sm hover:bg-gray-100 w-full px-2 py-1">
                            <FaUser className="mr-2" /> Profile
                        </button>
                        <button className="flex items-center text-gray-700 text-sm hover:bg-gray-100 w-full px-2 py-1">
                            <FaCog className="mr-2" /> Setting
                        </button>
                        <button className="flex items-center text-gray-700 text-sm hover:bg-gray-100 w-full px-2 py-1">
                            <FaQuestionCircle className="mr-2" /> Help
                        </button>
                        <button
                            onClick={handleLogout}
                            disabled={logoutLoading}
                            className={`flex items-center w-full px-2 py-1 text-sm 
  ${logoutLoading ? "opacity-60 cursor-not-allowed" : "text-red-600 hover:bg-gray-100"}`}
                        >
                            <FaSignOutAlt className="mr-2" />
                            {logoutLoading ? "Signing out..." : "Log Out"}
                        </button>
                    </div>
                </div>
            </nav >
        </>

    );
};

export default Navbar;





// import React, { useEffect, useRef, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { FaUserCircle, FaBell } from 'react-icons/fa';
// import { useAuth } from '../../context/AuthContext';
// import { logoutUser } from '../../services/authService';

// const Navbar = () => {
//     const [setUser] = useState(null);
//     const [dropdownOpen, setDropdownOpen] = useState(false);
//     const dropdownRef = useRef(null);
//     const { user, refreshToken, logout } = useAuth();
//     const [showLang, setShowLang] = useState(true);
//     const [selectedLang, setSelectedLang] = useState("en");

//     const navigate = useNavigate();

//     // Call backend to invalidate refresh token
//     const handleLogout = async () => {
//         try {
//             await logoutUser(refreshToken); // <-- backend logout
//         } catch (err) {
//             console.error('Logout API failed', err);
//         }
//         logout(); // <-- clear from context/localStorage
//         // setTimeout(() => {
//         navigate('/login');
//         // }, 100);
//     };

//     // Equivalent of ng-change="setLang(Selectedlang)"
//     const setLang = (lang) => {
//         console.log("Selected language:", lang);
//         // you can add your logic here (e.g., API call, i18n change, etc.)
//     };

//     useEffect(() => {
//         const handleClickOutside = (event) => {
//             if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//                 setDropdownOpen(false);
//             }
//         };
//         document.addEventListener('mousedown', handleClickOutside);
//         return () => document.removeEventListener('mousedown', handleClickOutside);
//     }, []);

//     if (!user) return null;


//     return (
//         <nav className="bg-white shadow-md px-6 py-4 flex justify-between items-center">
//             <div>
//                 <strong></strong>
//             </div>
//             <div className="relative flex" ref={dropdownRef}>
//                 <div>
//                     <table className='mr-[20px] border-l-0 border-t-0 border-b-0 border-r-0'>
//                         <tbody>
//                             <tr>
//                                 <td className='text-center cursor-pointer p-[10px] block '>
//                                     <select
//                                         id="Selectedlang"
//                                         name="Selectedlang"
//                                         value={selectedLang}
//                                         onChange={(e) => {
//                                             setSelectedLang(e.target.value);
//                                             setLang(e.target.value);
//                                         }}
//                                         className="block w-60 rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-800 shadow-[2px_2px_7px_#8acafb] focus:outline-none focus:ring-2 focus:ring-blue-400"
//                                         style={{ display: showLang ? "block" : "none" }}
//                                     >
//                                         <option value="en">English</option>
//                                         <option value="gj">ગુજરાતી</option>
//                                         <option value="mr">मराठी</option>
//                                         <option value="hn">हिन्दी</option>
//                                     </select>

//                                 </td>
//                             </tr>
//                         </tbody>
//                     </table>
//                 </div>
//                 <button
//                     onClick={() => setDropdownOpen(!dropdownOpen)}
//                     className="flex items-center space-x-2 text-gray-700 focus:outline-none"
//                 >
//                     <FaUserCircle className="text-2xl" />
//                 </button>
//                 {dropdownOpen && (
//                     <div className="absolute top-10 right-0 bg-white border shadow-md rounded-md w-64 p-4 z-10">
//                         <h2 className="text-lg font-semibold">{user.username}</h2>
//                         <p className="text-sm text-gray-500">{user.sub}</p>
//                         <button
//                             onClick={handleLogout}
//                             className="mt-4 w-full bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
//                         >
//                             Logout
//                         </button>
//                     </div>
//                 )}
//             </div>
//         </nav>
//     );
// };

// export default Navbar;
